import { useLayoutEffect, useRef } from 'react';

interface ManualExpandScrollOptions {
  intent?: number;
  loading?: boolean;
  explicitNavigation?: boolean;
  displayedPulseIds: number[];
  onComplete?: (intent: number) => void;
  // react-window must be positioned before looking for a row: it may not be mounted.
  positionLastItem?: (index: number) => void;
  getListViewport?: () => HTMLElement | null;
}

/** Consume a manual expand once, after data and the displayed rows are ready. */
export function useManualExpandScroll({
  intent,
  loading = false,
  explicitNavigation = false,
  displayedPulseIds,
  onComplete,
  positionLastItem,
  getListViewport,
}: ManualExpandScrollOptions) {
  const completedIntent = useRef<number>();
  const pending = intent !== undefined && completedIntent.current !== intent;

  useLayoutEffect(() => {
    if (!pending || intent === undefined) return;
    // Explicit project/pin navigation takes precedence, including while loading.
    if (!explicitNavigation && loading) return;

    completedIntent.current = intent;
    if (!explicitNavigation && displayedPulseIds.length > 0) {
      const lastIndex = displayedPulseIds.length - 1;
      positionLastItem?.(lastIndex);
      const row = document.getElementById(`pulse-row-${displayedPulseIds[lastIndex]}`);
      // A regular pulse wrapper can also contain its expanded task tree. Reveal
      // the pulse header itself rather than the bottom of those child tasks.
      const pulseHeader = row?.querySelector<HTMLElement>('.tree-item') ?? row;
      // Initial offset mounts the last virtual row on first render. If an already
      // mounted list needs a React update, its viewport is an equivalent outer-page
      // target: scrollToItem has already positioned its last item at the bottom.
      (pulseHeader ?? getListViewport?.())?.scrollIntoView({ behavior: 'instant', block: 'end' });
    }
    onComplete?.(intent);
  }, [pending, intent, loading, explicitNavigation, displayedPulseIds, onComplete, positionLastItem, getListViewport]);

  return pending && !loading && !explicitNavigation;
}